import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { GetMyWorkspaces } from '../redux/usersSlice';
const ProtectedRoute = ({ children, allowedRoles }) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const refresh = Cookies.get('refresh');

    const memberships = useSelector(state => state?.user?.memberships || []);
    const workspacesLoading = useSelector(state => state?.user?.workspacesLoading);
    const activeWorkspaceId = useSelector(state => state?.user?.activeWorkspaceId) || localStorage.getItem('active_workspace_id');

    const workspaceIdInSetupPath = (() => {
        if (!location.pathname.startsWith('/workspace-setup/')) return null;
        const parts = location.pathname.split('/');
        return parts[2] || null;
    })();
    const workspaceSelectionRequired =
        useSelector(state => state?.user?.workspaceSelectionRequired) || localStorage.getItem('workspace_selection_required') === 'true';

    React.useEffect(() => {
        if (refresh && !workspacesLoading && memberships.length === 0) {
            dispatch(GetMyWorkspaces());
        }
    }, [refresh, workspacesLoading, memberships.length, dispatch]);

    if (!refresh) {
        // If user is trying to access a shared document link, preserve the shareId
        // as a query param so we can send them back after login.
        if (location.pathname.startsWith('/shared/document/')) {
            const segments = location.pathname.split('/');
            const shareId = segments[segments.length - 1] || '';
            const target = shareId ? `/login?doc=${encodeURIComponent(shareId)}` : '/login';
            return <Navigate to={target} replace />;
        }
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Shared document link: allow immediately so logged-in users opening the link
    // in a new tab are not sent to select-workspace (no workspace/state required yet).
    if (location.pathname.startsWith('/shared/document/')) {
        return <Outlet />;
    }

    // Coming from shared document flow (e.g. just navigated to /dataroom with fromSharedLink state):
    // allow so we don't redirect to select-workspace before state has settled.
    if (location.state?.fromSharedLink === true) {
        return <Outlet />;
    }

    // Allow workspace selection routes without requiring a selected workspace.
    if (location.pathname === '/select-workspace' || location.pathname === '/no-workspace-access') {
        return <Outlet />;
    }

    // If we haven't loaded workspaces yet, wait to avoid redirecting to login.
    if (workspacesLoading) {
        return null;
    }

    // If workspace selection is explicitly required, force user back to picker,
    // unless they are on the shared document loader (they should land there first).
    if (workspaceSelectionRequired && !location.pathname.startsWith('/shared/document/')) {
        return <Navigate to="/select-workspace" state={{ from: location }} replace />;
    }

    // Authenticated + multiple workspaces + no selection => force workspace selection,
    // EXCEPT for shared document loader, which should work without selecting a workspace first.
    if (memberships.length > 1 && !activeWorkspaceId) {
        if (location.pathname.startsWith('/shared/document/')) {
            return <Outlet />;
        }
        if (
            workspaceIdInSetupPath &&
            memberships.some(m => String(m?.workspace?.id) === String(workspaceIdInSetupPath))
        ) {
            return <Outlet />;
        }
        return <Navigate to="/select-workspace" state={{ from: location }} replace />;
    }

    // Workspace super admin must complete first-time setup before using the app.
    if (
        activeWorkspaceId &&
        !location.pathname.startsWith('/workspace-setup/') &&
        location.pathname !== '/select-workspace' &&
        location.pathname !== '/no-workspace-access' &&
        !location.pathname.startsWith('/shared/document/')
    ) {
        const activeMembership = memberships.find(m => String(m?.workspace?.id) === String(activeWorkspaceId));
        const needsSetup =
            activeMembership?.role === 'WORKSPACE_SUPER_ADMIN' && activeMembership?.workspace?.is_setuped === false;
        if (needsSetup) {
            return <Navigate to={`/workspace-setup/${activeWorkspaceId}`} replace />;
        }
    }

    return <Outlet />;
};
export default ProtectedRoute;
